import { DocumentExtractionStatus, UserDocumentWithId } from "@/shared/types/documentTypes";
import { DocumentType } from "@/shared/types/documentTypes";
import { isExtractionReadable } from "@/shared/utils/documentUtils";
import { Card, CardTitle, CardHeader, CardContent, CardFooter } from "../ui/card";
import { Loader2, Sparkles, ThumbsUp, ThumbsDown } from "lucide-react";
import { useEffect, useState } from "react";
import { TextAnimate } from "@/shared/components/ui/text-animate";
import { supabase } from "@/shared/lib/supabase";
import { IconButton } from "@/shared/components/ui/icon-button";
import { AnimatedShinyText } from "@/shared/components/ui/animated-shiny-text";
import { enqueueSnackbar } from "notistack";
import DynamicDocumentInsights from "./DynamicDocumentInsights";
import WillInsights from "./WillInsights";
import DeathCertificateInsights from "./DeathCertificateInsights";
import CreditReportInsights from "./CreditReportInsights";
import InvestmentAccountStatementInsights from "./InvestmentAccountStatementInsights";
import BankStatementInsights from "./BankStatementInsights";
import CreditCardStatementInsights from "./CreditCardStatementInsights";
import InsuranceDocumentInsights from "./InsuranceDocumentInsights";
import { TrustInsights } from "./TrustInsights";
import { TrustAmendmentInsights } from "./TrustAmendmentInsights";
import { IdentificationCardInsights } from "./IdentificationCardInsights";
import TPAAuthorizationInsights from "./TPAAuthorizationInsights";
import { PaymentReceiptInsights } from "./PaymentReceiptInsights";
import { unwrapExtraction } from "@/shared/utils/unwrapExtraction";

export default function DocumentSummaryAndInsights({ document }: { document: UserDocumentWithId }) {
  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <DocumentSummaryContent document={document} />
    </div>
  );
}

function getTPASignatureType(document: UserDocumentWithId): "tpa" | "irs" | null {
  if (document.documentType !== DocumentType.THIRD_PARTY_AUTHORIZATION) {
    return null;
  }
  const name = (document.display_name ?? "").toLowerCase();
  return name.startsWith("irs authorization") ? "irs" : "tpa";
}

function DocumentSummaryContent({ document }: { document: UserDocumentWithId }) {
  const tpaSignatureType = getTPASignatureType(document);
  if (tpaSignatureType) {
    return <TPAAuthorizationInsights signatureType={tpaSignatureType} document={document} />;
  }
  if (document.documentType === DocumentType.PAYMENT_RECEIPT) {
    return <PaymentReceiptInsights documentId={document.id} />;
  }

  const isExtractionInProgress =
    document.extractionStatus === DocumentExtractionStatus.ExtractionInProgress;
  const isExtractionComplete =
    document.extractionStatus === DocumentExtractionStatus.ExtractionComplete ||
    (isExtractionInProgress &&
      isExtractionReadable(document.smart_summary?.status, !!document.specific_doc_extraction));

  if (isExtractionInProgress && !isExtractionComplete) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
          <p className="text-center !text-muted-foreground max-w-[420px]">
            Analyzing your document...
          </p>
        </div>
      </div>
    );
  }

  if (isExtractionComplete) {
    return (
      <>
        <ExtractedDocumentSummary document={document} />
        <p className="text-2xs text-tertiary px-8 py-6">{LEGAL_DISCLAIMER}</p>
      </>
    );
  }

  return <BasicDocumentSummary document={document} />;
}

const LEGAL_DISCLAIMER =
  "Elayne is not a law firm and does not provide legal advice. The information shown here is extracted automatically and may contain errors. Consult a qualified attorney for legal guidance.";

function ExtractedDocumentSummary({ document }: { document: UserDocumentWithId }) {
  if (document.documentType === DocumentType.CREDIT_REPORT) {
    const creditReportData = unwrapExtraction(document.specific_doc_extraction);
    return (
      <div className="space-y-0">
        <BasicDocumentSummary document={document} />
        {creditReportData ? (
          <CreditReportInsights
            creditReportData={creditReportData}
            citations={document.specific_doc_citations}
          />
        ) : (
          <div className="px-6 py-6 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-brand" />
            <p className="text-sm text-secondary">Analyzing document...</p>
          </div>
        )}
      </div>
    );
  }

  if (document.documentType === DocumentType.WILL) {
    const willData = unwrapExtraction(document.specific_doc_extraction);
    const contacts = document.document_extraction?.contacts?.contacts;
    return (
      <div className="space-y-0">
        <BasicDocumentSummary document={document} />
        {willData ? (
          <WillInsights
            willData={willData}
            contacts={contacts}
            citations={document.specific_doc_citations}
          />
        ) : (
          <div className="px-6 py-6 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-brand" />
            <p className="text-sm text-secondary">Analyzing document...</p>
          </div>
        )}
      </div>
    );
  }

  if (document.documentType === DocumentType.DEATH_CERTIFICATE) {
    const deathCertData = unwrapExtraction(document.specific_doc_extraction);
    const contacts = document.document_extraction?.contacts?.contacts;
    return (
      <div className="space-y-0">
        <BasicDocumentSummary document={document} />
        {deathCertData ? (
          <DeathCertificateInsights
            data={deathCertData}
            contacts={contacts}
            citations={document.specific_doc_citations}
          />
        ) : (
          <div className="px-6 py-6 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-brand" />
            <p className="text-sm text-secondary">Analyzing document...</p>
          </div>
        )}
      </div>
    );
  }

  if (document.documentType === DocumentType.INVESTMENT_ACCOUNT_STATEMENT) {
    const investmentData = unwrapExtraction(document.specific_doc_extraction);
    return (
      <div className="space-y-0">
        <BasicDocumentSummary document={document} />
        {investmentData ? (
          <InvestmentAccountStatementInsights
            data={investmentData}
            citations={document.specific_doc_citations}
          />
        ) : (
          <div className="px-6 py-6 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-brand" />
            <p className="text-sm text-secondary">Analyzing document...</p>
          </div>
        )}
      </div>
    );
  }

  if (document.documentType === DocumentType.BANK_STATEMENT) {
    const bankStatementData = unwrapExtraction(document.specific_doc_extraction);
    return (
      <div className="space-y-0">
        <BasicDocumentSummary document={document} />
        {bankStatementData ? (
          <BankStatementInsights
            data={bankStatementData}
            citations={document.specific_doc_citations}
          />
        ) : (
          <div className="px-6 py-6 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-brand" />
            <p className="text-sm text-secondary">Analyzing document...</p>
          </div>
        )}
      </div>
    );
  }

  if (document.documentType === DocumentType.CREDIT_CARD_STATEMENT) {
    const creditCardData = unwrapExtraction(document.specific_doc_extraction);
    return (
      <div className="space-y-0">
        <BasicDocumentSummary document={document} />
        {creditCardData ? (
          <CreditCardStatementInsights
            data={creditCardData}
            citations={document.specific_doc_citations}
          />
        ) : (
          <div className="px-6 py-6 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-brand" />
            <p className="text-sm text-secondary">Analyzing document...</p>
          </div>
        )}
      </div>
    );
  }

  if (document.documentType === DocumentType.INSURANCE_DOCUMENT) {
    const insuranceData = unwrapExtraction(document.specific_doc_extraction);
    return (
      <div className="space-y-0">
        <BasicDocumentSummary document={document} />
        {insuranceData ? (
          <InsuranceDocumentInsights
            data={insuranceData}
            citations={document.specific_doc_citations}
          />
        ) : (
          <div className="px-6 py-6 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-brand" />
            <p className="text-sm text-secondary">Analyzing document...</p>
          </div>
        )}
      </div>
    );
  }

  if (document.documentType === DocumentType.TRUST) {
    const trustData = unwrapExtraction(document.specific_doc_extraction);
    return (
      <div className="space-y-0">
        <BasicDocumentSummary document={document} />
        {trustData ? (
          <TrustInsights data={trustData} citations={document.specific_doc_citations} />
        ) : (
          <div className="px-6 py-6 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-brand" />
            <p className="text-sm text-secondary">Analyzing document...</p>
          </div>
        )}
      </div>
    );
  }

  if (document.documentType === DocumentType.TRUST_AMENDMENT) {
    const trustAmendmentData = unwrapExtraction(document.specific_doc_extraction);
    return (
      <div className="space-y-0">
        <BasicDocumentSummary document={document} />
        {trustAmendmentData ? (
          <TrustAmendmentInsights
            data={trustAmendmentData}
            citations={document.specific_doc_citations}
          />
        ) : (
          <div className="px-6 py-6 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-brand" />
            <p className="text-sm text-secondary">Analyzing document...</p>
          </div>
        )}
      </div>
    );
  }

  if (
    document.documentType === DocumentType.IDENTIFICATION_CARD ||
    document.documentType === DocumentType.DECEDENT_ID
  ) {
    const idData = unwrapExtraction(document.specific_doc_extraction);
    return (
      <div className="space-y-0">
        <BasicDocumentSummary document={document} />
        {idData ? (
          <IdentificationCardInsights data={idData} citations={document.specific_doc_citations} />
        ) : (
          <div className="px-6 py-6 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-brand" />
            <p className="text-sm text-secondary">Analyzing document...</p>
          </div>
        )}
      </div>
    );
  }

  const unwrappedExtraction = unwrapExtraction(document.specific_doc_extraction);
  if (unwrappedExtraction) {
    return (
      <div className="space-y-0">
        <BasicDocumentSummary document={document} />
        <DynamicDocumentInsights
          data={unwrappedExtraction}
          citations={document.specific_doc_citations}
        />
      </div>
    );
  }

  return <BasicDocumentSummary document={document} />;
}

function BasicDocumentSummary({ document }: { document?: UserDocumentWithId }) {
  const smartSummary = document?.smart_summary;
  const status = smartSummary?.status;
  const [feedback, setFeedback] = useState<"not_answered" | "good" | "bad">(
    smartSummary?.feedback || "not_answered",
  );
  const [isUpdatingFeedback, setIsUpdatingFeedback] = useState(false);

  useEffect(() => {
    setFeedback(smartSummary?.feedback || "not_answered");
  }, [smartSummary?.feedback]);

  const updateFeedback = async (newFeedback: "good" | "bad") => {
    if (!document?.id || isUpdatingFeedback) return;

    setIsUpdatingFeedback(true);

    try {
      const { data: currentDoc, error: fetchError } = await supabase
        .from("estate_documents")
        .select("smart_summary")
        .eq("id", document.id)
        .single();

      if (fetchError) {
        console.error("Failed to fetch current document:", fetchError);
        return;
      }

      const updatedSummary = {
        ...currentDoc.smart_summary,
        feedback: newFeedback,
      };

      const { error: updateError } = await supabase
        .from("estate_documents")
        .update({ smart_summary: updatedSummary })
        .eq("id", document.id);

      if (updateError) {
        console.error("Failed to update feedback:", updateError);
        return;
      }

      setFeedback(newFeedback);

      enqueueSnackbar("Feedback received", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error updating feedback:", error);
    } finally {
      setIsUpdatingFeedback(false);
    }
  };

  if (status === "pending" || status === "processing") {
    return (
      <div className="p-6">
        <Card className="border-primary shadow-none">
          <CardHeader className="bg-brand-subtle/70 rounded-t-xl shadow-none">
            <CardTitle className="font-[500] leading-none text-base flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-brand" />
              <AnimatedShinyText>Document summary</AnimatedShinyText>
            </CardTitle>
          </CardHeader>
          <CardContent className="bg-brand-subtle/70 rounded-b-xl shadow-none">
            <div className="flex flex-col items-center justify-center space-y-3 py-4">
              <Loader2 className="h-8 w-8 animate-spin text-brand" />
              <div className="text-center">
                <p className="text-primary font-medium">Processing document...</p>
                <p className="text-sm text-secondary mt-1">
                  Analyzing document and generating summary
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="p-6">
        <Card className="border-primary shadow-none">
          <CardHeader className="bg-brand-subtle/70 rounded-t-xl shadow-none">
            <CardTitle className="font-[500] leading-none text-base flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-brand" />
              <AnimatedShinyText>Document summary</AnimatedShinyText>
            </CardTitle>
          </CardHeader>
          <CardContent className="bg-brand-subtle/70 rounded-b-xl shadow-none">
            <p className="text-primary font-light leading-relaxed">
              This document appears to contain very little or no readable text. It may be an
              image-based document or scanned file. We're unable to generate a summary at this time.
            </p>
          </CardContent>
          {feedback === "not_answered" && (
            <CardFooter className="bg-white rounded-b-xl py-3 flex items-center justify-between shadow-none">
              <p className="text-primary font-light leading-relaxed">Was this summary helpful?</p>
              <div className="flex items-center gap-2">
                <IconButton
                  onClick={() => updateFeedback("good")}
                  disabled={isUpdatingFeedback}
                  label="Mark as helpful"
                >
                  <ThumbsUp />
                </IconButton>
                <IconButton
                  onClick={() => updateFeedback("bad")}
                  disabled={isUpdatingFeedback}
                  label="Mark as unhelpful"
                >
                  <ThumbsDown />
                </IconButton>
              </div>
            </CardFooter>
          )}
        </Card>
      </div>
    );
  }

  if (status === "completed" && smartSummary?.summary) {
    return (
      <CompletedSummary
        summary={smartSummary.summary}
        feedback={feedback}
        onFeedback={updateFeedback}
        isUpdatingFeedback={isUpdatingFeedback}
      />
    );
  }

  return (
    <div className="p-6">
      <Card className="border-primary shadow-none">
        <CardHeader className="bg-brand-subtle/70 rounded-t-xl shadow-none">
          <CardTitle className="font-[500] leading-none text-base flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-brand" />
            <AnimatedShinyText>Document summary</AnimatedShinyText>
          </CardTitle>
        </CardHeader>
        <CardContent className="bg-brand-subtle/70 rounded-b-xl shadow-none">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-brand" />
            <p className="text-secondary">Generating summary...</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CompletedSummary({
  summary,
  feedback,
  onFeedback,
  isUpdatingFeedback,
}: {
  summary: string;
  feedback: "not_answered" | "good" | "bad";
  onFeedback: (f: "good" | "bad") => void;
  isUpdatingFeedback: boolean;
}) {
  const [animationDone, setAnimationDone] = useState(false);

  return (
    <div className="p-6">
      <Card className="border-primary shadow-none">
        <CardHeader className="bg-brand-subtle/70 rounded-t-xl shadow-none">
          <CardTitle className="font-[500] leading-none text-base flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-brand" />
            <AnimatedShinyText>Document summary</AnimatedShinyText>
          </CardTitle>
        </CardHeader>
        <CardContent className="bg-brand-subtle/70 rounded-b-xl shadow-none">
          {animationDone ? (
            <p className="font-light leading-relaxed text-left">{summary}</p>
          ) : (
            <TextAnimate
              as="p"
              by="word"
              animation="fadeIn"
              className="font-light leading-relaxed"
              startOnView={false}
              duration={0.8}
              onAnimationComplete={() => setAnimationDone(true)}
            >
              {summary}
            </TextAnimate>
          )}
        </CardContent>
        {feedback === "not_answered" && (
          <CardFooter className="bg-white rounded-b-xl py-3 flex items-center justify-between shadow-none">
            <p className="text-primary font-light leading-relaxed">Was this summary helpful?</p>
            <div className="flex items-center gap-2">
              <IconButton
                onClick={() => onFeedback("good")}
                disabled={isUpdatingFeedback}
                label="Mark as helpful"
              >
                <ThumbsUp />
              </IconButton>
              <IconButton
                onClick={() => onFeedback("bad")}
                disabled={isUpdatingFeedback}
                label="Mark as unhelpful"
              >
                <ThumbsDown />
              </IconButton>
            </div>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}
